import { Exam, Workflow } from '@parsys/frontend_library';

type GetExamParams = {
  workflow: Workflow;
  translationOnlyExams?: Record<string, Exam>
  /**
  * @deprecated TransitionId is no longer necessary and should be avoided unless you are certain the exam you are trying to get
  * will ONLY be scoped to a specific transition (which should almost never happen)
  */
  transitionId?: string;
  examId: string;
  /**
   * Skip the module-level cache for both reading and writing. Use when the caller needs a
   * guaranteed-fresh resolve from the current `workflow`
   */
  skipCache?: boolean;
}

// In memory caching
const examCache = {};

export const getExam = ({
  workflow,
  translationOnlyExams,
  transitionId,
  examId,
  skipCache = false,
}: GetExamParams): Exam | null => {
  // Check the in memory cache
  if (!skipCache && examCache[examId]) {
    return examCache[examId];
  }
  // waiting for station.forms to be hydrated from the cloud
  if (!workflow || !examId) {
    return null;
  }
  let exam: Exam | null = null;

  // we begin by the least expensive search: is the exam a translation only exam?
  if (translationOnlyExams?.[examId]) {
    if (!skipCache) {
      examCache[examId] = translationOnlyExams?.[examId];
    }

    return translationOnlyExams?.[examId];
  }
  // if we don't have any information on the transition, we need to find it first
  if (!transitionId) {
    const transitions = workflow.transitions;
    // loop through the workflow transitions until we find our exam
    for (const transitionKey in transitions) {
      exam = transitions[transitionKey].exams_full.find((exam) => exam.id === examId) ?? null;
      // upon finding it, we break early, no need to traverse the remaining transitions
      if (exam) {
        if (!skipCache) {
          examCache[examId] = exam;
        }
        break;
      }
    }
    // keeping the transition path for retrocompatility/eventual cases where a feature doesn't make sense in several transitions
    // but we should aim to avoid using it in the future
  } else {
    // We know the transition where the exam resides, either from passing a genericFieldPath or directly hardcoded (the latter is legacy, don't do this)
    const transition = workflow.transitions[transitionId];
    // if we can't find the transition or don't know what we are looking for, no sense in continuing
    if (!transition || !examId) {
      return null;
    }
    // We try to find the examIdentifier inside the transition exams_full key
    exam = transition.exams_full.find((exam) => exam.id === examId) ?? null;
    if (!exam) {
      return null;
    }
    if (!skipCache) {
      examCache[examId] = exam;
    }
  }

  return exam;
};
